import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Drawer } from '../../app/Drawer';
import { DrawerLink } from '../../app/DrawerLink';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Verified } from '../../components/ui/Verified';
import { subscribeToUser } from '../../lib/realtime';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { startJam, useMyJam } from '../jam/useJam';
import { FEED_KEY, activity, type Activity, type ListeningContext } from './api';

/**
 * What your friends are hearing: a panel from the header.
 *
 * Friends are mutual follows, so this is the one social surface that fills
 * up without anybody writing anything, and on this platform the tracks in
 * it are mostly other artists here, which makes it discovery as much as
 * company. A friend in a live room is a door; a friend in a jam is a
 * one-tap join. The jam you are in, or the button to start one, sits at
 * the top: listening together is what the panel is for.
 *
 * Polled while open; a socket event on your own channel is a hint to
 * refetch, but the list is a presence and thirty seconds late is fine.
 *
 * ## The same drawer as a track
 *
 * `/activity`, opened over whatever page you are on with a background
 * location, exactly as a track row opens `/tracks/123`: the URL is
 * shareable, the back button closes it, and a cold load renders the full
 * page. Everything a drawer needs (scrim, Escape, focus, the dock left
 * reachable) is `Drawer`'s and not repeated here.
 */
export function ActivityButton() {
  const user = useSession((s) => s.user);

  if (!user) return null;

  return (
    <>
      <DrawerLink
        to="/activity"
        aria-label="Friends' listening activity"
        data-activity-button
        className="relative flex h-9 w-9 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink"
      >
        <svg viewBox="0 0 20 20" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="7" cy="7" r="2.6" />
          <path d="M2.5 16.5c0-2.8 2-4.5 4.5-4.5s4.5 1.7 4.5 4.5" />
          <circle cx="14" cy="6.5" r="2.2" />
          <path d="M12.5 11.8c2.6-.3 5 1.4 5 4.2" />
        </svg>
      </DrawerLink>
    </>
  );
}

/** The drawer route, over a background; and the full page, without one. */
export function ActivityDrawer() {
  return (
    <Drawer title="Listening activity" fullPath="/activity">
      <ActivityBody />
    </Drawer>
  );
}

export function ActivityPage() {
  return (
    <div className="mx-auto max-w-[28rem] px-4 py-6" data-activity-page>
      <h1 className="mb-3 text-xl font-semibold text-ink">Listening activity</h1>
      <ActivityBody />
    </div>
  );
}

function ActivityBody() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const me = useSession((s) => s.user);
  const refreshUser = useSession((s) => s.refreshUser);

  const feed = useQuery({
    queryKey: FEED_KEY,
    queryFn: () => activity.feed(),
    // Fresh on every open: a presence read from a thirty-second cache is a
    // friend shown listening to something they stopped.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!me) return;

    return subscribeToUser(me.id, () => {
      void client.invalidateQueries({ queryKey: FEED_KEY });
    });
  }, [me, client]);

  const privateSession = useMutation({
    mutationFn: (seconds: number | null) => activity.privately(seconds),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: FEED_KEY });
      await refreshUser();
    },
  });

  const items = feed.data?.items ?? [];
  const sharing = feed.data?.sharing ?? true;
  const settingOff = me?.share_listening === false;

  // Navigation from inside a drawer: the drawer's own URL is the top of the
  // history, so going somewhere from it is a plain navigate; the page behind
  // is what a later back returns to.
  const go = (to: string) => navigate(to);

  return (
    <div className="-mx-5 -mt-5 flex flex-col" data-activity-panel>
          <JamCard go={go} />

          {/* Your own row, and the private session. */}
          <div className="border-b border-line px-5 py-3" data-activity-mine>
            {settingOff ? (
              <p className="text-xs text-faint">
                You are not sharing what you listen to.{' '}
                <Link to="/settings" className="text-accent hover:underline">
                  Settings
                </Link>
              </p>
            ) : !sharing ? (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-faint">Private session: friends see nothing until it ends.</p>
                <Button variant="ghost" loading={privateSession.isPending} onClick={() => privateSession.mutate(null)} data-private-off>
                  End it
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 truncate text-xs text-faint">
                  {feed.data?.mine ? (
                    <>
                      Friends see you on <span className="text-soft">{feed.data.mine.track.title}</span>
                    </>
                  ) : (
                    'Friends see what you play.'
                  )}
                </p>
                <Button
                  variant="ghost"
                  loading={privateSession.isPending}
                  onClick={() => privateSession.mutate(6 * 3600)}
                  title="Hide what you listen to for six hours"
                  className="shrink-0 whitespace-nowrap"
                  data-private-on
                >
                  Private session
                </Button>
              </div>
            )}
          </div>

          {feed.isPending && <p className="px-5 py-8 text-center text-sm text-faint">Looking…</p>}

          {feed.isSuccess && items.length === 0 && (
            <div className="px-5 py-10 text-center" data-activity-empty>
              <p className="text-sm text-ink">Nothing yet.</p>
              <p className="mt-1 text-xs leading-relaxed text-faint">
                This shows what people you follow are playing, once they follow you back. Find some
                artists, and it fills up on its own.
              </p>
            </div>
          )}

          <ul className="flex flex-col" data-activity-list>
            {items.map((item) => (
              <ActivityRow key={item.user.username} item={item} go={go} />
            ))}
          </ul>
    </div>
  );
}

function ActivityRow({ item, go }: { item: Activity; go: (to: string) => void }) {
  const play = usePlayer((s) => s.play);

  return (
    <li className="flex gap-3 border-b border-line px-5 py-3.5" data-activity-row={item.user.username}>
      <button type="button" onClick={() => go(`/${item.user.username}`)} className="relative shrink-0 self-start">
        <Avatar src={item.user.avatar} name={item.user.display_name} size={40} />
        {item.now && (
          <span
            className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-surface bg-fresh"
            title="Listening now"
            data-activity-now
          />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <button type="button" onClick={() => go(`/${item.user.username}`)} className="truncate text-sm font-semibold text-ink hover:underline">
            {item.user.display_name}
            {item.user.verified && <Verified />}
          </button>
          <span className="shrink-0 text-xs text-faint">{item.now ? 'now' : ago(item.heard_at)}</span>
        </div>

        <button
          type="button"
          onClick={() =>
            void play(
              {
                id: item.track.id,
                title: item.track.title,
                duration_ms: null,
                waveform_url: null,
                artwork: item.track.artwork_small,
                artwork_small: item.track.artwork_small,
                owner: item.track.owner,
              },
              undefined,
              'feed',
            )
          }
          className="mt-0.5 block max-w-full truncate text-left text-sm text-soft hover:text-ink"
          title={`Play ${item.track.title}`}
        >
          {item.track.title} <span className="text-faint">· {item.track.owner.display_name}</span>
        </button>

        <Context context={item.context} go={go} />
      </div>
    </li>
  );
}

/** Where they are hearing it, as a line with an icon, and a door where there is one. */
function Context({ context, go }: { context: ListeningContext; go: (to: string) => void }) {
  const cls = 'mt-1 flex items-center gap-1.5 text-xs text-faint';

  switch (context.kind) {
    case 'collection':
      return (
        <button type="button" onClick={() => go(`/playlists/${context.id}`)} className={`${cls} hover:text-ink`}>
          <NoteGlyph /> <span className="truncate">{context.label}</span>
        </button>
      );
    case 'room':
      return (
        <button type="button" onClick={() => go(`/${context.username}`)} className={`${cls} text-hot-text hover:underline`} data-activity-room>
          <LiveGlyph /> <span className="truncate">in {context.label}&apos;s room · join</span>
        </button>
      );
    case 'jam':
      return (
        <button type="button" onClick={() => go(`/jam/${context.code}`)} className={`${cls} text-accent hover:underline`} data-activity-jam>
          <JamGlyph /> <span>in a jam · join</span>
        </button>
      );
    default:
      return null;
  }
}

/**
 * The jam you are in, or the way to start one. Starting is one call; the
 * page it lands on has the link to send.
 */
function JamCard({ go }: { go: (to: string) => void }) {
  const client = useQueryClient();
  const { data } = useMyJam();
  const jam = data?.jam ?? null;

  const start = useMutation({
    mutationFn: () => startJam(client),
    onSuccess: (made) => go(`/jam/${made.code}`),
  });

  const host = jam?.members.find((m) => m.is_host);

  return (
    <div className="border-b border-line bg-raised/60 px-5 py-4" data-activity-jam-card>
      {jam ? (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink">{host ? `${host.display_name}'s jam` : 'Your jam'}</p>
            <p className="text-xs text-faint">
              {jam.members.length} in · {jam.queue.length} queued
            </p>
          </div>
          <Button onClick={() => go(`/jam/${jam.code}`)} data-open-jam>
            Open
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs leading-relaxed text-soft">Invite friends to a jam and listen together, in time, from anywhere.</p>
          <Button loading={start.isPending} onClick={() => start.mutate()} className="shrink-0 whitespace-nowrap" data-start-jam>
            Start a jam
          </Button>
        </div>
      )}
    </div>
  );
}

function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso.replace(' ', 'T') + 'Z').getTime()) / 1000);

  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))} min`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} hr`;

  return `${Math.floor(seconds / 86_400)} d`;
}

const glyph = { viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true } as const;

function NoteGlyph() {
  return (
    <svg {...glyph} className="h-3.5 w-3.5 shrink-0">
      <path d="M6 12V3.5l7-1.5v8" />
      <circle cx="4.5" cy="12" r="1.5" />
      <circle cx="11.5" cy="10" r="1.5" />
    </svg>
  );
}

function LiveGlyph() {
  return (
    <svg {...glyph} className="h-3.5 w-3.5 shrink-0">
      <circle cx="8" cy="8" r="1.5" fill="currentColor" />
      <path d="M4.5 4.5a5 5 0 0 0 0 7M11.5 4.5a5 5 0 0 1 0 7" />
    </svg>
  );
}

function JamGlyph() {
  return (
    <svg {...glyph} className="h-3.5 w-3.5 shrink-0">
      <path d="M2 5.5h7l-2-2M14 10.5H7l2 2" />
    </svg>
  );
}
